import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { STEPS } from "../lib/checkData";
import { useCheck } from "../contexts/CheckContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import Seo from "../components/Seo";

export default function CheckPage() {
    const { answers, setAnswer, setTeaser } = useCheck();
    const navigate = useNavigate();

    const visibleSteps = useMemo(
        () => STEPS.filter((s) => !s.showIf || s.showIf(answers)),
        [answers],
    );
    const [idx, setIdx] = useState(0);
    const step = visibleSteps[idx];
    const progress = ((idx + 1) / visibleSteps.length) * 100;
    const currentValue = answers[step?.id];
    const canNext = currentValue !== undefined && currentValue !== "";

    const onNext = async () => {
        if (idx < visibleSteps.length - 1) {
            setIdx((i) => i + 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        // last step → strip empty values and calculate teaser
        const cleaned = Object.fromEntries(
            Object.entries(answers).filter(
                ([, v]) => v !== "" && v !== null && v !== undefined,
            ),
        );
        try {
            const { data } = await api.post("/cases/calculate", { answers: cleaned });
            setTeaser(data);
            navigate("/teaser");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const onPrev = () => {
        if (idx > 0) {
            setIdx((i) => i - 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            navigate("/");
        }
    };

    if (!step) return null;

    return (
        <div className="min-h-[80vh] bg-brand-50/30 py-6 sm:py-10">
            <Seo
                title="Kostenloser Pflegeheimkosten-Check"
                description="In wenigen Minuten zur ersten Einschätzung: Prüfen Sie diskret, ob Pflegeheimkosten für Sie relevant werden. 10 Fragen zu Einkommen, Vermögen, Pflegegrad und Region."
                path="/check"
            />
            <div className="container max-w-2xl">
                {/* Progress */}
                <div className="mb-6">
                    <div className="flex items-center justify-between text-xs text-ink-500">
                        <span>Schritt {idx + 1} von {visibleSteps.length}</span>
                        <span className="inline-flex items-center gap-1">
                            <ShieldCheck className="h-3.5 w-3.5 text-brand-500" /> Diskret &amp; anonym
                        </span>
                    </div>
                    <Progress value={progress} className="mt-2 h-2 bg-brand-100" data-testid="check-progress" />
                </div>

                {/* Card */}
                <div className="rounded-3xl border border-ink-300/30 bg-white p-6 shadow-softer sm:p-8 animate-fade-in-up">
                    <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">
                        {step.title}
                    </div>
                    <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                        {step.question}
                    </h2>
                    {step.hint && (
                        <p className="mt-3 rounded-xl bg-brand-50 px-4 py-3 text-sm text-ink-700">
                            {step.hint}
                        </p>
                    )}

                    {step.type === "single" && (
                        <div className="mt-6 flex flex-col gap-3" role="radiogroup">
                            {step.options.map((opt) => {
                                const selected = currentValue === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={selected}
                                        data-selected={selected}
                                        data-testid={`check-option-${step.id}-${opt.value}`}
                                        onClick={() => setAnswer(step.id, opt.value)}
                                        className="radio-card group flex items-center justify-between rounded-2xl border border-ink-300/40 bg-white px-5 py-4 text-left transition hover:border-brand-300"
                                    >
                                        <span className="font-medium text-ink-900">{opt.label}</span>
                                        <span
                                            className={`flex h-6 w-6 flex-none items-center justify-center rounded-full border-2 ${
                                                selected ? "border-brand-500 bg-brand-500" : "border-ink-300"
                                            }`}
                                        >
                                            {selected && <span className="h-2 w-2 rounded-full bg-white" />}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {step.type === "number" && (
                        <div className="mt-6">
                            <Input
                                type="number"
                                inputMode="numeric"
                                min={step.min}
                                max={step.max}
                                value={currentValue ?? ""}
                                onChange={(e) =>
                                    setAnswer(step.id, e.target.value === "" ? "" : Number(e.target.value))
                                }
                                placeholder="z. B. 2"
                                className="h-14 rounded-2xl text-base"
                                data-testid={`check-number-${step.id}`}
                            />
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex items-center justify-between gap-3">
                    <Button
                        variant="ghost"
                        onClick={onPrev}
                        className="rounded-full gap-2"
                        data-testid="check-prev"
                    >
                        <ArrowLeft className="h-4 w-4" /> Zurück
                    </Button>
                    <Button
                        onClick={onNext}
                        disabled={!canNext}
                        className="rounded-full bg-brand-500 hover:bg-brand-600 gap-2 h-12 px-6"
                        data-testid="check-next"
                    >
                        {idx < visibleSteps.length - 1 ? "Weiter" : "Meine Einschätzung zeigen"}
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </div>

                <p className="mt-8 text-center text-xs text-ink-400">
                    Ihre Angaben werden ausschließlich zur Einordnung Ihrer Situation verwendet.
                    Keine automatisierte Rechtsberatung.
                </p>
            </div>
        </div>
    );
}
